import { BarChart, DonutChart, PieChart } from '@mantine/charts'
import { Center, Paper, Text } from '@mantine/core'
import { AbnormalBadge } from '@/components/charts/AbnormalBadge'
import { TrendLineChart } from '@/components/charts/TrendLineChart'
import {
  formatChartNumber,
  formatChartReading,
  formatUtcDate,
  hasAbnormalPoints,
  isTimeAxisData,
  normalizeCartesianData,
  toLabel,
  toNumber,
  type LabDatum,
} from '@/components/charts/trend-chart.utils'
import type { ChartSpec } from './assistant-chart.utils'

type AssistantChartProps = {
  chart: ChartSpec
}

const SERIES_COLOR = 'blue.6'
const SEGMENT_COLORS = [
  'blue.6',
  'teal.6',
  'violet.6',
  'orange.6',
  'red.6',
  'cyan.6',
  'grape.6',
  'lime.7',
]

const normalizeRadialData = (chart: ChartSpec) =>
  chart.data.flatMap((datum, index) => {
    const value = toNumber(datum[chart.yKey])
    if (value === null) return []

    return {
      name: toLabel(datum[chart.xKey], index),
      value,
      color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
    }
  })

// min/max of the plotted value series, shown on the right of the title row for cartesian
// charts (radial segment values have no meaningful aggregate). A qualified reading ('<5') is a
// limit, not an exact value, so it is never the min or max; with no exact reading there is none.
const seriesStats = (
  data: ReadonlyArray<Record<string, unknown>>,
  yKey: string,
): { min: number; max: number } | null => {
  const values = data
    .filter((datum) => (datum as LabDatum).comparator == null)
    .map((datum) => toNumber(datum[yKey]))
    .filter((value): value is number => value !== null)
  if (values.length === 0) return null
  return { min: Math.min(...values), max: Math.max(...values) }
}

const StatEntry = ({ label, value }: { label: string; value: number | string }) => (
  <>
    <span className="text-slate-500">{label}:</span>{' '}
    <span className="font-medium text-slate-900">
      {typeof value === 'number' ? formatChartNumber(value) : value}
    </span>
  </>
)

// The most recent reading of a date-keyed line chart, for the header's centered "latest" hint.
// Producers emit data chronologically, but assistant-authored charts may not — pick the max date.
// A qualified reading keeps its comparator ('<5').
const latestReading = (
  data: ReadonlyArray<Record<string, unknown>>,
  xKey: string,
  yKey: string,
): { value: string; date: string } | null => {
  let latest: { value: string; ms: number } | null = null
  for (const datum of data) {
    const value = toNumber(datum[yKey])
    const raw = datum[xKey]
    const ms = typeof raw === 'string' || typeof raw === 'number' ? Date.parse(String(raw)) : NaN
    if (value === null || Number.isNaN(ms)) continue
    if (!latest || ms >= latest.ms)
      latest = { value: formatChartReading(value, (datum as LabDatum).comparator), ms }
  }
  return latest ? { value: latest.value, date: formatUtcDate(latest.ms) } : null
}

export const AssistantChart = ({ chart }: AssistantChartProps) => {
  const hasTitle = chart.title.trim().length > 0
  const cartesianData =
    chart.type === 'bar' || chart.type === 'line'
      ? normalizeCartesianData(chart.data, chart.xKey, chart.yKey)
      : []
  const radialData =
    chart.type === 'pie' || chart.type === 'donut' ? normalizeRadialData(chart) : []
  const hasData = cartesianData.length > 0 || radialData.length > 0
  // Abnormal marker shown in the header for lab line charts with ≥1 out-of-range point — the same
  // indicator the Clinical Summary surfaces.
  const showAbnormal = chart.type === 'line' && hasData && hasAbnormalPoints(chart.data)
  // Date-keyed line charts get a time axis so horizontal gaps reflect the real interval between
  // points; non-date line charts (rare) stay categorical to avoid dropping/misplacing points.
  const useTimeAxis = chart.type === 'line' && isTimeAxisData(cartesianData, chart.xKey)
  const stats = seriesStats(cartesianData, chart.yKey)
  // Centered "latest" hint — only meaningful for date-keyed line charts.
  const latest = useTimeAxis ? latestReading(cartesianData, chart.xKey, chart.yKey) : null

  return (
    <Paper withBorder radius="md" className="overflow-hidden">
      {(hasTitle || showAbnormal || stats) && (
        <div className="border-b px-3.5 py-2.5">
          {/* Four header blocks — name, latest/date, min/max, abnormal flag — spread across the
              row; when the width can't fit them they wrap onto extra lines (the header grows)
              instead of colliding. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <Text size="sm" fw={700} className="shrink-0">
              {hasTitle ? chart.title : ''}
              {chart.subTitle && (
                <span className="ml-2 text-xs font-normal text-slate-500">{chart.subTitle}</span>
              )}
            </Text>
            {latest && (
              <Text size="xs" className="shrink-0 whitespace-nowrap">
                <StatEntry label="latest" value={latest.value} />{' '}
                <StatEntry label="on" value={latest.date} />
              </Text>
            )}
            {stats && (
              <Text size="xs" className="shrink-0 whitespace-nowrap">
                <StatEntry label="min" value={stats.min} />{' '}
                <StatEntry label="max" value={stats.max} />
              </Text>
            )}
            {showAbnormal && <AbnormalBadge size="sm" />}
          </div>
        </div>
      )}
      <div className="px-2 py-3" aria-label={hasTitle ? chart.title : 'Assistant chart'}>
        {!hasData ? (
          <Center h={90}>
            <Text size="xs" c="dimmed">
              No chart data to display.
            </Text>
          </Center>
        ) : chart.type === 'bar' ? (
          <BarChart
            h={130}
            data={cartesianData}
            dataKey={chart.xKey}
            series={[{ name: chart.yKey, color: SERIES_COLOR }]}
            valueFormatter={formatChartNumber}
            tickLine="none"
            gridAxis="y"
            maxBarWidth={48}
          />
        ) : chart.type === 'line' ? (
          <TrendLineChart
            data={cartesianData}
            xKey={chart.xKey}
            yKey={chart.yKey}
            h={130}
            xScale={useTimeAxis ? 'time' : 'category'}
            valueLabel={hasTitle ? chart.title : undefined}
          />
        ) : chart.type === 'pie' ? (
          <Center h={130}>
            <PieChart
              data={radialData}
              size={110}
              withLabels
              labelsType="percent"
              valueFormatter={formatChartNumber}
            />
          </Center>
        ) : chart.type === 'donut' ? (
          <Center h={130}>
            <DonutChart
              data={radialData}
              size={110}
              thickness={19}
              withLabels
              labelsType="percent"
              valueFormatter={formatChartNumber}
            />
          </Center>
        ) : (
          <Center h={90}>
            <Text size="xs" c="dimmed">
              Unsupported chart type.
            </Text>
          </Center>
        )}
      </div>
    </Paper>
  )
}
